import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { LINK_IS_COPIED } from '../../constants/messages';
import { AppDispatch, RootState } from '../../store/store';
import { wishListHandler } from '../../store/UserSlice';
import { CartItem, Product, AlertType } from '../../types/common';
import { YM_ID } from '../metrics/ym';
import Header from '../shared/Header/Header';
import Footer from '../shared/Footer/Footer';
import Section from '../layouts/showcaseLayouts/Section/Section';
import SectionBody from '../layouts/showcaseLayouts/Section/SectionBody/SectionBody';
import AddToCartBtn from '../showcase/AddToCartBtn/AddToCartBtn';
import FavoriteIcon from '../UI/icons/FavoriteIcon/FavoriteIcon';
import NotFound from '../pages/showcasePages/NotFound/NotFound';
// import InfoBlock from '../pages/showcasePages/ProductPage/InfoBlock/InfoBlock';
import { fetchProductBySlug, clearRedirect } from '../../store/ProductSlice';
import { Helmet } from 'react-helmet-async';
import { buildFillerTypeFilterUrl, buildFabricTypeFilterUrl } from '../../utils/helpers';
import Gallery from './components/Gallery/Gallery';
import ProductReviews from './components/Reviews/ProductReviews';
import ProductTabs, { type ProductTabSection } from './components/ProductTabs/ProductTabs';
import { selectPublishedReviews } from '../../store/ReviewsSlice';
import { PATHS } from '../../constants/routes';
import Breadcrumbs, { BreadcrumbItem } from '../shared/Breadcrumbs/Breadcrumbs';
import { showAlert } from '../../store/CommonSlice';
// import PromoProducts from './components/promoProducts/PromoProducts';
import PromoProducts from '../HomePage/promoProducts/PromoProducts';
import BackButton from '../shared/backButton/BackButton';
import useIsLTE650 from '../../hooks/useIsLTE650';
import './productSlugPage.scss';

interface IProductPageProps {}

export type Discount = {
    percent: number;
    discountedPrice: number;
} | null;

declare global {
    interface Window {
        prerenderReady?: boolean;
        dataLayer?: any[];
    }
}

const getImageUrl = (imageId: string) => `${process.env.REACT_APP_SERVER_URL}/products/images/${imageId}`;

const parseCareInstructions = (instructions: string): React.ReactNode => {
    const matches = Array.from(instructions.matchAll(/(\d+)\./g));
    if (matches.length === 0) {
        return <p style={{ paddingLeft: '1rem' }}>{instructions}</p>;
    }
    let isValidSequence = true;
    for (let i = 0; i < matches.length; i++) {
        const number = parseInt(matches[i][1], 10);
        if (number !== i + 1) {
            isValidSequence = false;
            break;
        }
    }
    if (!isValidSequence) {
        return <p style={{ paddingLeft: '1rem' }}>{instructions}</p>;
    }
    const parts = instructions.split(/\s*\d+\.\s*/).filter(Boolean);
    return parts.map((part, index) => (
        <p key={index} style={{ paddingLeft: '1rem', margin: '0.2rem' }}>
            <strong>{index + 1}.</strong> {part.trim()}
        </p>
    ));
};

const looksLikeObjectId = (s?: string) => Boolean(s && /^[a-fA-F0-9]{24}$/.test(s!));

const isPositiveNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;
const hasDiscount = (discount: Discount | undefined): discount is NonNullable<Discount> => {
    return !!discount && (isPositiveNumber(discount.percent) || isPositiveNumber(discount.discountedPrice));
};

const pickDiscount = (special?: Discount | null, regular?: Discount | null): NonNullable<Discount> | null => {
    if (hasDiscount(special)) return special!;
    if (hasDiscount(regular)) return regular!;
    return null;
};

const parseSize = (val: unknown): [number, number] | null => {
    if (typeof val !== 'string') return null;
    const s = val.trim().toLowerCase().replace(',', '.');
    const m = s.match(/(\d+(?:\.\d+)?)\s*[xх×*]\s*(\d+(?:\.\d+)?)/i);
    if (m) {
        const w = parseFloat(m[1]);
        const h = parseFloat(m[2]);
        if (Number.isFinite(w) && Number.isFinite(h)) return [w, h];
    }
    const m1 = s.match(/^\s*(\d+(?:\.\d+)?)\s*$/);
    if (m1) {
        const w = parseFloat(m1[1]);
        if (Number.isFinite(w)) return [w, 0];
    }
    return null;
};
const collator = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' });
const compareVariants = (a: Product, b: Product) => {
    const aStr = String(a.group?.optionValue ?? a.name ?? '').trim();
    const bStr = String(b.group?.optionValue ?? b.name ?? '').trim();
    const aSize = parseSize(aStr);
    const bSize = parseSize(bStr);
    if (aSize && bSize) {
        if (aSize[0] !== bSize[0]) return aSize[0] - bSize[0];
        if (aSize[1] !== bSize[1]) return aSize[1] - bSize[1];
        return 0;
    }
    return collator.compare(aStr, bStr);
};

const renderDescriptionParagraphs = (text?: string | null): React.ReactNode => {
    if (!text) return null;
    const normalized = text.replace(/\r\n/g, '\n');
    const paragraphs = normalized
        .split(/\n\s*\n/g)
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
    if (!paragraphs.length) {
        return <p className="product-page__text">{text}</p>;
    }
    return paragraphs.map((paragraph, paragraphIndex) => {
        const lines = paragraph.split(/\n+/);
        const content = lines.reduce<React.ReactNode[]>((acc, line, lineIndex) => {
            acc.push(line);
            if (lineIndex < lines.length - 1) {
                acc.push(<br key={`${paragraphIndex}-${lineIndex}`} />);
            }
            return acc;
        }, []);
        return (
            <p key={paragraphIndex} className="product-page__text">
                {content}
            </p>
        );
    });
};

const ProductSlugPade: React.FC<IProductPageProps> = () => {
    const { slug: slugParam, id: productIdParam } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const navOnce = useRef<string | null>(null);
    const reviewsHashScrollTimerRef = useRef<number | null>(null);
    const { products, isLoading, missedSlugs, inFlightSlugs, redirectTo } = useSelector((state: RootState) => state.product);
    const { wishlist } = useSelector((state: RootState) => state.user);
    const productBySlug = slugParam ? products.find((p) => p.slug === slugParam) : undefined;
    const productById = productIdParam ? products.find((p) => p.id === productIdParam) : undefined;
    const product = (productBySlug || productById) as Product | undefined;
    const isMobile = useIsLTE650(650);
    /* Берём длину, а не сам массив: селектор отдаёт новый [] при промахе,
       и подписка на массив дёргала бы ререндер на каждом экшене */
    const reviewsCount = useSelector((state: RootState) => (product ? selectPublishedReviews(state, product.id).length : 0));

    // const [activeSpecifications, setActiveSpecifications] = useState(true);
    const activeSpecifications = true;

    useEffect(() => {
        if (!redirectTo) return;
        if (navOnce.current === redirectTo) return;
        navOnce.current = redirectTo;
        navigate(redirectTo, { replace: true });
        dispatch(clearRedirect());
    }, [redirectTo, navigate, dispatch]);

    useEffect(() => {
        if (!slugParam) return;
        if (looksLikeObjectId(slugParam)) return;
        if (productBySlug) return;
        if (missedSlugs[slugParam]) return;
        if (inFlightSlugs[slugParam]) return;
        try {
            window.prerenderReady = false;
        } catch {}
        dispatch(fetchProductBySlug(slugParam));
    }, [slugParam, productBySlug, missedSlugs, inFlightSlugs, dispatch]);

    useEffect(() => {
        if (!slugParam && productById?.slug) {
            navigate(`/${productById.slug}`, { replace: true });
        }
    }, [slugParam, productById, navigate]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!product) return;
        if (location.hash !== '#reviews') return;
        let attempts = 0;
        let cancelled = false;
        const scrollToReviews = () => {
            if (cancelled) return;
            const element = document.getElementById('reviews');
            if (element) {
                const top = Math.max(0, element.getBoundingClientRect().top + window.scrollY - 120);
                window.scrollTo({ top, behavior: attempts === 0 ? 'auto' : 'smooth' });
            }
            attempts += 1;
            if (attempts < 10) {
                reviewsHashScrollTimerRef.current = window.setTimeout(scrollToReviews, 180);
            }
        };
        scrollToReviews();
        return () => {
            cancelled = true;
            if (reviewsHashScrollTimerRef.current !== null) {
                window.clearTimeout(reviewsHashScrollTimerRef.current);
                reviewsHashScrollTimerRef.current = null;
            }
        };
    }, [location.hash, product]);

    // ---- PRERENDER: готовность страницы товара ----
    useEffect(() => {
        try {
            window.prerenderReady = Boolean(product);
        } catch {}
    }, [product, slugParam, productIdParam]);

    // Отправка e-commerce события detail (просмотр карточки товара) в Яндекс.Метрику через dataLayer
    useEffect(() => {
        if (process.env.NODE_ENV !== 'production') return;
        if (!YM_ID) return;
        if (!product) return;
        try {
            const { id, name, price, discount, specialDiscount, category, fillerType, fabricType, articleNumber, group } = product;
            const effectiveDiscount = pickDiscount(specialDiscount, discount);
            const effectiveUnitPrice = effectiveDiscount?.discountedPrice ?? price;
            const productIdForEcom = articleNumber || id;
            if (!productIdForEcom || !name || !isPositiveNumber(effectiveUnitPrice)) return;
            const categoryName = category?.name ? String(category.name) : undefined;
            const typeNameLabel = fillerType?.name || fabricType?.name ? String(fillerType?.name || fabricType?.name) : undefined;
            const variantLabel = group?.optionValue ? String(group.optionValue) : undefined;
            const w = window as any;
            w.dataLayer = w.dataLayer || [];
            w.dataLayer.push({
                ecommerce: {
                    currencyCode: 'RUB',
                    detail: {
                        products: [
                            {
                                id: String(productIdForEcom),
                                name: String(name),
                                price: Number(effectiveUnitPrice),
                                ...(typeNameLabel ? { brandName: typeNameLabel } : {}),
                                ...(categoryName ? { category: categoryName } : {}),
                                ...(variantLabel ? { variant: variantLabel } : {}),
                            },
                        ],
                    },
                },
            });
        } catch {
            // ignore
        }
    }, [product]);

    if (!product) {
        if (slugParam && !looksLikeObjectId(slugParam)) {
            if (missedSlugs[slugParam]) {
                return <NotFound />;
            }
            if (inFlightSlugs[slugParam] || isLoading || !missedSlugs[slugParam]) {
                return null;
            }
        }
        return <NotFound />;
    }

    const groupId = product.group?.id;
    const allVariants: Product[] = groupId ? products.filter((p) => p.group?.id === groupId) : [];
    const sortedVariants = allVariants.slice().sort(compareVariants);

    const {
        id,
        slug,
        name,
        nameDescription,
        description,
        fillerType,
        fabricType,
        price,
        weight,
        discount,
        specialDiscount,
        countryOrigin,
        brandName,
        articleNumber,
        category,
        sellerName,
        productСolor,
        rawMaterial,
        careInstructions,
        complectation,
        group,
    } = product;

    const quantity = (product as any)?.quantity;
    const isInStock = typeof quantity === 'number' ? quantity > 0 : true;

    const isWished = wishlist.includes(id);
    const reviewsEnabled = product.reviewsEnabled === true;

    /* Единственный источник секций: на десктопе это табы, до 650px - аккордеон */
    const tabSections: ProductTabSection[] = [];
    if (description) {
        tabSections.push({ id: 'description', label: 'Описание', content: renderDescriptionParagraphs(description) });
    }
    if (careInstructions) {
        tabSections.push({ id: 'care', label: 'Уход', content: <div className="product-page__text">{parseCareInstructions(careInstructions)}</div> });
    }
    if (sellerName || countryOrigin) {
        tabSections.push({
            id: 'seller',
            label: 'Информация о продавце',
            content: (
                <p className="product-page__text">
                    {sellerName && `Продавец: ${sellerName}.`}
                    <br />
                    {countryOrigin && `Страна-изготовитель: ${countryOrigin}.`}
                </p>
            ),
        });
    }
    if (reviewsEnabled) {
        tabSections.push({
            id: 'reviews',
            label: 'Отзывы',
            badge: reviewsCount,
            content: <ProductReviews productId={id} reviewsEnabled={reviewsEnabled} isMobile={isMobile} embedded />,
        });
    }
    const hasReviewsSection = tabSections.some((section) => section.id === 'reviews');

    const images = [
        product.image ? getImageUrl(product.image) : null,
        product.secondImage ? getImageUrl(product.secondImage) : null,
        product.thirdImage ? getImageUrl(product.thirdImage) : null,
        product.fourthImage ? getImageUrl(product.fourthImage) : null,
        product.fifthImage ? getImageUrl(product.fifthImage) : null,
        product.sixthImage ? getImageUrl(product.sixthImage) : null,
    ].filter(Boolean) as string[];

    const effectiveDiscount = pickDiscount(specialDiscount, discount);
    const effectiveUnitPrice = effectiveDiscount?.discountedPrice ?? price;
    const effectivePercent =
        typeof effectiveDiscount?.percent === 'number' && Number.isFinite(effectiveDiscount.percent)
            ? effectiveDiscount.percent
            : effectiveDiscount?.discountedPrice
              ? Math.round((1 - effectiveDiscount.discountedPrice / price) * 100)
              : undefined;
    const cartItem: CartItem = {
        productId: id,
        name,
        nameDescription,
        quantity: 1,
        price: effectiveUnitPrice,
        totalPrice: effectiveUnitPrice,
        weight,
        totalWeight: weight,
        discountedPrice: discount?.discountedPrice,
        discount: discount?.percent,
        specialDiscountPercent: specialDiscount?.percent,
        specialDiscountedPrice: specialDiscount?.discountedPrice,
    };

    const handleVariantClick = (variant: Product) => {
        if (variant.id === product.id) return;
        navigate(`/${variant.slug}`);
        try {
            window.prerenderReady = false;
        } catch {}
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // const handleBackClick = () => {
    //     navigate(-1);
    // };

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const pageUrl = `${origin}/${slug}`;
    const fillerTypeFilterHref = buildFillerTypeFilterUrl(fillerType);
    const fabricTypeFilterHref = buildFabricTypeFilterUrl(fabricType);

    const SHOW_TYPE_CRUMBS = false; // скрыть/показать тип ткани/наполнителя в хлебных крошках
    // ХЛЕБНЫЕ КРОШКИ
    const breadcrumbItems: BreadcrumbItem[] = [
        { label: 'Главная', to: PATHS.main },
        { label: 'Все товары', to: PATHS.showcase || '/catalog' },
    ];
    if (category?.name && category?.url) {
        breadcrumbItems.push({
            label: category.name,
            to: `${PATHS.showcase || '/catalog'}/${category.url}`,
        });
    }
    if (SHOW_TYPE_CRUMBS && fabricType?.name && fabricTypeFilterHref) {
        breadcrumbItems.push({
            label: fabricType.name,
            to: fabricTypeFilterHref,
        });
    }
    if (SHOW_TYPE_CRUMBS && fillerType?.name && fillerTypeFilterHref) {
        breadcrumbItems.push({
            label: fillerType.name,
            to: fillerTypeFilterHref,
        });
    }
    // Текущий товар - финальный элемент без ссылки
    if (name) {
        breadcrumbItems.push({ label: name });
    }

    // КОПИРОВАНИЕ ССЫЛКИ В БУФЕР ОБМЕНА
    const copyToClipboard = async (text: string): Promise<boolean> => {
        try {
            if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                await navigator.clipboard.writeText(text);
                return true;
            }
        } catch {}
        try {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.setAttribute('readonly', '');
            ta.style.position = 'fixed';
            ta.style.top = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(ta);
            return ok;
        } catch {
            return false;
        }
    };
    const handleShareClick = async () => {
        const ok = await copyToClipboard(pageUrl);
        dispatch(
            showAlert({
                type: ok ? AlertType.Success : AlertType.Error,
                message: LINK_IS_COPIED,
            }),
        );
    };

    // --------- SEO: Product + BreadcrumbList ---------
    const imgObj = (product as any)?.images || {};
    const seoImagesList = [
        imgObj.image ? getImageUrl(imgObj.image) : null,
        imgObj.secondImage ? getImageUrl(imgObj.secondImage) : null,
        imgObj.thirdImage ? getImageUrl(imgObj.thirdImage) : null,
        imgObj.fourthImage ? getImageUrl(imgObj.fourthImage) : null,
        imgObj.fifthImage ? getImageUrl(imgObj.fifthImage) : null,
        imgObj.sixthImage ? getImageUrl(imgObj.sixthImage) : null,
    ].filter(Boolean) as string[];
    const seoImages = seoImagesList.length ? seoImagesList : images;
    const seoMainImage = seoImages[0];
    const sizeFromGroup = group?.url?.trim()?.toLowerCase() === 'размер' && group?.optionValue ? String(group.optionValue) : undefined;
    const additionalProps: Array<{ '@type': 'PropertyValue'; name: string; value: string }> = [];
    if (rawMaterial) additionalProps.push({ '@type': 'PropertyValue', name: 'Материал', value: String(rawMaterial) });
    if (sizeFromGroup) additionalProps.push({ '@type': 'PropertyValue', name: 'Размер', value: sizeFromGroup });
    if (complectation) additionalProps.push({ '@type': 'PropertyValue', name: 'Комплектация', value: String(complectation) });
    if (productСolor) additionalProps.push({ '@type': 'PropertyValue', name: 'Дизайн, цвет', value: String(productСolor) });
    if (countryOrigin) additionalProps.push({ '@type': 'PropertyValue', name: 'Страна-изготовитель', value: String(countryOrigin) });
    if (typeof weight === 'number') additionalProps.push({ '@type': 'PropertyValue', name: 'Вес', value: `${weight} кг` });
    if (brandName) additionalProps.push({ '@type': 'PropertyValue', name: 'Торговая марка', value: String(brandName) });
    if (category?.name) additionalProps.push({ '@type': 'PropertyValue', name: 'Категория', value: String(category.name) });
    const seoPrice = discount?.discountedPrice ?? price;
    const material = fabricType?.name || fillerType?.name || undefined;
    const productLd = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': `${pageUrl.replace(/\/?$/, '/')}#product`,
        name: name,
        description: description || nameDescription || undefined,
        image: seoImages.length ? seoImages : undefined,
        sku: articleNumber || undefined,
        brand: { '@type': 'Brand', name: brandName || 'S-Gabai' },
        ...(material ? { material } : {}),
        offers: {
            '@type': 'Offer',
            url: pageUrl.replace(/\/?$/, '/'),
            priceCurrency: 'RUB',
            price: Number(seoPrice),
            availability: isInStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
            itemCondition: 'https://schema.org/NewCondition',
        },
        additionalProperty: additionalProps.length ? additionalProps : undefined,
    };

    const absHref = (href?: string | null) => {
        if (!href) return undefined;
        return href.startsWith('http') ? href : `${origin}${href}`;
    };
    const fabricTypeCrumbAbs = SHOW_TYPE_CRUMBS ? absHref(fabricTypeFilterHref) : undefined;
    const fillerTypeCrumbAbs = SHOW_TYPE_CRUMBS ? absHref(fillerTypeFilterHref) : undefined;
    const crumbs: Array<{ name: string; item: string }> = [];
    crumbs.push({ name: 'Главная', item: `${origin}/` });
    crumbs.push({ name: 'Каталог', item: `${origin}/catalog/` });
    if (category?.name && category?.url) {
        crumbs.push({ name: category.name, item: `${origin}/catalog/${category.url}/` });
    }
    if (SHOW_TYPE_CRUMBS && fabricType?.name && fabricTypeCrumbAbs) {
        crumbs.push({ name: fabricType.name, item: fabricTypeCrumbAbs });
    }
    if (SHOW_TYPE_CRUMBS && fillerType?.name && fillerTypeCrumbAbs) {
        crumbs.push({ name: fillerType.name, item: fillerTypeCrumbAbs });
    }
    crumbs.push({ name: name, item: pageUrl.replace(/\/?$/, '/') });
    const breadcrumbList = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: crumbs.map((c, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: c.name,
            item: c.item,
        })),
    };

    return (
        <>
            <Header isMobile={isMobile} />
            <Section>
                <>
                    <Helmet>
                        <title>
                            {name} – купить {category?.name || 'шелковые изделия'} с доставкой по России
                        </title>
                        <meta
                            name="description"
                            content={`Купите ${name}${
                                category?.name ? ` в категории «${category.name}»` : ''
                            } на сайте S-Gabai. Шелк Mulberry или Tussah, премиальное качество, доставка по всей России. Оформите заказ онлайн или по тел.: +7 495 280-60-55`}
                        />
                        <link rel="canonical" href={`${pageUrl.replace(/\/?$/, '/')}`} />
                        <meta property="og:type" content="product" />
                        <meta property="og:title" content={name} />
                        <meta property="og:url" content={pageUrl.replace(/\/?$/, '/')} />
                        {seoMainImage && <meta property="og:image" content={seoMainImage} />}
                        <script type="application/ld+json">{JSON.stringify(productLd)}</script>
                        <script type="application/ld+json">{JSON.stringify(breadcrumbList)}</script>
                    </Helmet>
                    <SectionBody>
                        <>
                            <div className="product-page">
                                <div className="product-page__nav">
                                    {/* <button className="product-page__back-button" title="Назад" onClick={handleBackClick}>
                                        <img className="product-page__back-button_img" src="/icons/arrow-left.svg" alt="" />
                                    </button> */}
                                    <BackButton />
                                    <Breadcrumbs items={breadcrumbItems} />
                                </div>
                                <Gallery
                                    images={images}
                                    wrapperClassName="product-page__gallery"
                                    imageClassName="product-page__image"
                                    arrowClassName="product-page__arrow-button"
                                    indicatorClassName="product-page__indicator"
                                    indicatorActiveClassName="product-page__indicator--active"
                                    ariaLabel={`Галерея товара: ${name}`}
                                    autoPlay={false}
                                    showArrows
                                    showIndicators
                                />
                                <div className="product-page__content">
                                    {/* <div className="product-page__content_notification">
                                        <img className="product-page__content_notification_img" src="/icons/attention.png" alt="внимание" />
                                        <span className="product-page__content_notification_text">
                                            Доставка заказов, оформленных после{' '}
                                            <br className="product-page__content_notification_break product-page__content_notification_break_mobile" />
                                            29 декабря, <br className="product-page__content_notification_break" />
                                            осуществляется после 12 января.
                                        </span>
                                    </div> */}
                                    <div className="product-page__content_wrapper-control">
                                        <div className="product-page__title-wrap">
                                            <div className="product-page__content_control">
                                                <button
                                                    className="product-page__content_control_toFavorites"
                                                    onClick={() => dispatch(wishListHandler({ id, isWished }))}
                                                >
                                                    <FavoriteIcon filled={isWished} height={24} />
                                                </button>
                                                <button
                                                    className="product-page__content_control_linkShare"
                                                    onClick={handleShareClick}
                                                    title="Поделиться"
                                                    aria-label="Скопировать ссылку на товар"
                                                >
                                                    <img className="product-page__content_control_linkShare_img" src="/icons/link-2.svg" alt="" />
                                                </button>
                                            </div>
                                            <div className="product-page__chips">
                                                {fabricTypeFilterHref && fabricType?.name && (
                                                    <Link className="product-page__chips_elem" to={fabricTypeFilterHref} target="_blank">
                                                        {fabricType.name}
                                                    </Link>
                                                )}
                                                {fillerTypeFilterHref && fillerType?.name && (
                                                    <Link className="product-page__chips_elem" to={fillerTypeFilterHref} target="_blank">
                                                        {fillerType.name}
                                                    </Link>
                                                )}
                                                {articleNumber && (
                                                    <span className="product-page__chips_elem" title={'Артикул товара'}>
                                                        {articleNumber}
                                                    </span>
                                                )}
                                            </div>
                                            <h1 className="product-page__title" title={name}>
                                                {name}
                                            </h1>
                                            <p className="product-page__name-desc" title={nameDescription}>
                                                {nameDescription}
                                            </p>
                                        </div>
                                        <div className="product-page__price-row">
                                            <div className="product-page__content_price">
                                                {effectiveDiscount ? (
                                                    <div className="product-page__content_price_currentCost">{effectiveUnitPrice.toLocaleString()} ₽</div>
                                                ) : (
                                                    <div className="product-page__content_price_cost">{price.toLocaleString()} ₽</div>
                                                )}
                                                {effectiveDiscount && <div className="product-page__content_price_previousCost">{price.toLocaleString()} ₽</div>}
                                                {effectiveDiscount && typeof effectivePercent === 'number' && (
                                                    <span className="product-page__content_price_discount">-{effectivePercent}%</span>
                                                )}
                                            </div>
                                            <AddToCartBtn product={cartItem} />
                                        </div>
                                        {/* <div className="product-page__accordions"> */}
                                        <div className={`product-page__section ${activeSpecifications ? 'product-page__section--active' : ''}`}>
                                            {/* <p
                                                className="product-page__section-title product-page__section-title_mobile"
                                                onClick={() => setActiveSpecifications(!activeSpecifications)}
                                            >
                                                О товаре
                                                <span className={`product-page__section-arrow ${activeSpecifications ? 'product-page__section-arrow--open' : ''}`}>
                                                    &#8249;
                                                </span>
                                            </p> */}

                                            {category && category.name && (
                                                <div className="product-page__spec">
                                                    <span className="product-page__spec-title">Категория</span>
                                                    <span className="product-page__spec-value">{category.name}</span>
                                                </div>
                                            )}

                                            {articleNumber && (
                                                <div className="product-page__spec">
                                                    <span className="product-page__spec-title">Артикул</span>
                                                    <span className="product-page__spec-value">{articleNumber}</span>
                                                </div>
                                            )}

                                            {allVariants.length > 0 && (
                                                <div className="product-page__spec">
                                                    <span className="product-page__spec-title">{group?.url}</span>
                                                    <div className="product-page__variant-row">
                                                        {sortedVariants.map((v) => {
                                                            const isActive = v.id === product.id;
                                                            return (
                                                                <button
                                                                    key={v.id}
                                                                    className={`product-page__variant-btn ${isActive ? 'product-page__variant-btn--active' : ''}`}
                                                                    onClick={() => handleVariantClick(v)}
                                                                    disabled={isActive}
                                                                >
                                                                    {v.group?.optionValue || 'Вариант'}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {rawMaterial && (
                                                <div className="product-page__spec">
                                                    <span className="product-page__spec-title">Состав</span>
                                                    <span className="product-page__spec-value">{rawMaterial}</span>
                                                </div>
                                            )}

                                            {complectation && (
                                                <div className="product-page__spec">
                                                    <span className="product-page__spec-title">Комплектация</span>
                                                    <span className="product-page__spec-value">{complectation}</span>
                                                </div>
                                            )}

                                            {productСolor && (
                                                <div className="product-page__spec">
                                                    <span className="product-page__spec-title">Дизайн, цвет</span>
                                                    <span className="product-page__spec-value">{productСolor}</span>
                                                </div>
                                            )}

                                            {weight && (
                                                <div className="product-page__spec">
                                                    <span className="product-page__spec-title">Вес товара</span>
                                                    <span className="product-page__spec-value">{weight} кг</span>
                                                </div>
                                            )}

                                            {brandName && (
                                                <div className="product-page__spec">
                                                    <span className="product-page__spec-title">Торговая марка</span>
                                                    <span className="product-page__spec-value">{brandName}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {/* <InfoBlock popupStyles /> */}
                                </div>
                            </div>
                            {/* </div> */}

                            <ProductTabs
                                sections={tabSections}
                                isMobile={isMobile}
                                id={hasReviewsSection ? 'reviews' : undefined}
                                activeSectionId={location.hash === '#reviews' && hasReviewsSection ? 'reviews' : undefined}
                            />
                        </>
                    </SectionBody>
                </>
            </Section>
            <PromoProducts />
            <Footer isMobile={isMobile} />
        </>
    );
};

export default ProductSlugPade;
